import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAlertSubscriptions } from "./useAlertSubscriptions";
import { useNotificationChannels } from "./useNotificationChannels";

/** Shared toggle switch — role="switch" with focus-ring tokens matching
 *  AlertSubscriptionsCard. */
function Switch({
  checked,
  label,
  disabled,
  onClick,
}: {
  checked: boolean;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={
        (checked ? "bg-primary" : "bg-muted") +
        " h-5 w-9 rounded-full transition-colors outline-none" +
        " focus-visible:ring-[3px] focus-visible:ring-ring/50"
      }
    >
      <span
        className={
          "block h-4 w-4 rounded-full bg-background transition-transform " +
          (checked ? "translate-x-4" : "translate-x-0.5")
        }
      />
    </button>
  );
}

/** Preferences-page body for the "Notification channels" SettingsCard.
 *  The card title/description come from the parent (PreferencesPage). */
export function NotificationChannelsCard({ email }: { email: string | null }) {
  const { t } = useTranslation("preferences");
  const ch = useNotificationChannels();
  const subs = useAlertSubscriptions();
  // Store the deep_link returned by startLink() so we can render a
  // copyable fallback anchor during the waiting state (popup blockers
  // may have eaten the new tab — Task 7 requirement).
  const [deepLink, setDeepLink] = useState<string | undefined>(undefined);
  // In-flight guard: prevents double-click from opening two tabs / minting twice.
  const [linking, setLinking] = useState(false);

  // Dead state cleanup: once telegram is linked the waiting anchor is unreachable.
  useEffect(() => { if (ch.telegram?.linked) setDeepLink(undefined); }, [ch.telegram?.linked]);

  if (ch.locked) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted-foreground">{t("channels.locked")}</p>
        <Link to="/account/billing" className="text-sm underline">{t("channels.upgradeCta")}</Link>
      </div>
    );
  }

  const nothingEnabled =
    !ch.email?.enabled && !(ch.telegram?.linked && ch.telegram.enabled);
  const showWarning = subs.subscribed.size > 0 && nothingEnabled;

  function handleStartLink() {
    if (linking) return;
    setLinking(true);
    void ch
      .startLink()
      .then((link) => {
        if (link) setDeepLink(link);
      })
      .catch(() => {
        toast.error(t("channels.connectFailed"));
      })
      .finally(() => {
        setLinking(false);
      });
  }

  return (
    <div className="flex flex-col gap-3">
      {showWarning ? (
        <p className="text-sm text-amber-500">
          {t("channels.noChannelsWarning")}
        </p>
      ) : null}

      {/* Email row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className="text-sm">{t("channels.email")}</span>
          {email ? (
            <span className="text-xs text-muted-foreground">{email}</span>
          ) : null}
        </div>
        <Switch
          checked={!!ch.email?.enabled}
          label={t("channels.email")}
          disabled={ch.loading}
          onClick={() => void ch.setEnabled("email", !ch.email?.enabled)}
        />
      </div>

      {/* Telegram row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col">
          <span className="text-sm">{t("channels.telegram")}</span>
          {ch.telegram?.linked ? (
            <span className="text-xs text-muted-foreground">
              {ch.telegram.display_name ?? t("channels.connected")}
            </span>
          ) : ch.linkWaiting ? (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">
                {t("channels.waiting")}
              </span>
              {deepLink ? (
                <a
                  href={deepLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs underline text-muted-foreground"
                >
                  {t("channels.openManually")}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>

        {ch.telegram?.linked ? (
          <div className="flex items-center gap-2">
            <Switch
              checked={ch.telegram.enabled}
              label={t("channels.telegram")}
              disabled={ch.loading}
              onClick={() =>
                void ch.setEnabled("telegram", !ch.telegram?.enabled)
              }
            />
            <button
              type="button"
              aria-label={t("channels.disconnect")}
              className="text-xs text-muted-foreground underline outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              onClick={() => void ch.unlink()}
            >
              {t("channels.disconnect")}
            </button>
          </div>
        ) : ch.linkWaiting ? (
          <button
            type="button"
            className="text-xs underline outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            onClick={() => {
              setDeepLink(undefined);
              ch.cancelLinkWait();
            }}
          >
            {t("channels.cancel")}
          </button>
        ) : (
          <button
            type="button"
            aria-label={t("channels.connect")}
            disabled={linking}
            className="text-sm rounded-md border px-3 py-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            onClick={handleStartLink}
          >
            {t("channels.connect")}
          </button>
        )}
      </div>
    </div>
  );
}
